import { useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { GoabPageBlock, GoabSpacer } from "@abgov/react-components";
import { selectCurrentUser } from "../../Store/api/auth/authSelectors";
import NewApplicationListComponent from "../Application/NewApplicationListComponent";
import ApplicationList from "../Application/ApplicationList";
import { useGetApplicationListQuery } from "../../Store/api/application/applicationApi";
import { useGetProgramEventListQuery, useGetProgramFeatureMappingByNameQuery } from "../../Store/api/apiSlice";
import { selectProgram } from "../../Store/api/program/programSelectors";
import { getAvailableProgramEvents } from "../../MPP-Common-SPA/utils/getAvailableProgramEvents";
import { usePortalContext } from "../../PortalRunner/hooks/usePortalContext";
import { useSessionManagement } from "../../ServiceUtils/useSessionManagement";
import { EventsEnabled } from "../../MPP-Common-SPA/utils/globalUtility";
import { FeatureNames } from "../../MPP-Common-SPA/featureFlags/features";

export const Applications = () => {
  const user = useSelector(selectCurrentUser);
  const isLoggedIn = user?.isLoggedIn;
  
  const program = useSelector(selectProgram);
  const { data: applicationList, isLoading: isApplicationsLoading, isSuccess: isApplicationsLoaded, refetch: refetchApplications, isFetching: isApplicationsFetching, isUninitialized } = useGetApplicationListQuery(undefined, {
    skip: !isLoggedIn,
  });
  const { data: programEvents, isSuccess: isEventsLoaded } = useGetProgramEventListQuery(program?.id || '', {
    skip: !program?.id,
  });
  const { data: programEventMapping, isSuccess: isProgramEventMappingLoaded } = useGetProgramFeatureMappingByNameQuery(
    { featureName: FeatureNames.Events, programId: program?.id || '' }, 
    { skip: !program?.id }
  );

  const { withdrawApplication } = usePortalContext({});
  const { getResourceValue } = useSessionManagement();

  useEffect(() => {
    if (!isUninitialized && isLoggedIn) {
      refetchApplications();
    }
  }, [isUninitialized, isLoggedIn, refetchApplications]);

  const handleWithdrawApplication = (applicationId: any) => {
    withdrawApplication(applicationId).then((success: boolean) => {
      if (success) {
        refetchApplications();
      }
    })
  }

  const availableEvents = useMemo(() => {
    if (!isApplicationsLoaded || !isEventsLoaded || !isProgramEventMappingLoaded) return;
    
    return EventsEnabled(programEventMapping) && getAvailableProgramEvents(programEvents, applicationList);
  }, [applicationList, programEvents, isApplicationsLoaded, isEventsLoaded, isProgramEventMappingLoaded]) as [];

  return (
    <GoabPageBlock width="704px" testId="overviewContent">
      <>
        <GoabSpacer vSpacing="2xl" />
        <h1>{getResourceValue({ key: "RESOURCES@myApplications.title" })}</h1>
        <GoabSpacer vSpacing="m" />
        <h3>{getResourceValue({ key: "RESOURCES@myApplications.subTitle" })}</h3>
        <GoabSpacer vSpacing="2xl" />
        <ApplicationList applicationList={applicationList}
                         isLoading={isApplicationsLoading || isApplicationsFetching}
                         programEvents={programEvents}
                         handleWithdrawApplication={handleWithdrawApplication}
                         getResourceValue={getResourceValue}
                         program={program} 
                         user={user} />
      </>
      {!isLoggedIn && availableEvents && <NewApplicationListComponent availableEvents={availableEvents} getResourceValue={getResourceValue} program={program} user={user} isEventsEnabled={EventsEnabled(programEventMapping)}/>}
    </GoabPageBlock>
  );
};
